import { supabase } from './supabaseClient';
import type { CountyData, CropData } from './countyService';

// Enhanced data service that integrates with Supabase
export class DataService {
  // County-related methods
  static async getAllCounties(): Promise<CountyData[]> {
    try {
      const { data, error } = await supabase
        .from('counties')
        .select(`
          *,
          market_prices(*),
          farming_tips(*),
          resources(*)
        `);

      if (error) {
        console.error('Error fetching counties from database:', error);
        // Fallback to mock data
        return this.getMockCounties();
      }

      return data?.map(this.transformCountyData) || [];
    } catch (error) {
      console.error('Database connection error:', error);
      return this.getMockCounties();
    }
  }

  static async getCountyByName(name: string): Promise<CountyData | null> {
    try {
      const { data, error } = await supabase
        .from('counties')
        .select(`
          *,
          market_prices(*),
          farming_tips(*),
          resources(*)
        `)
        .ilike('name', name)
        .single();

      if (error) {
        console.error('Error fetching county from database:', error);
        return this.getMockCountyByName(name);
      }

      return data ? this.transformCountyData(data) : null;
    } catch (error) {
      console.error('Database connection error:', error);
      return this.getMockCountyByName(name);
    }
  }

  // Crop-related methods
  static async getCropData(name: string): Promise<CropData | null> {
    try {
      const { data, error } = await supabase
        .from('crops')
        .select(`
          *,
          crop_county_data(*),
          market_trends(*),
          growing_conditions(*),
          best_practices(*)
        `)
        .ilike('name', name)
        .single();

      if (error) {
        console.error('Error fetching crop from database:', error);
        return this.getMockCropData(name);
      }

      return data ? this.transformCropData(data) : null;
    } catch (error) {
      console.error('Database connection error:', error);
      return this.getMockCropData(name);
    }
  }

  static async getAllCrops(): Promise<CropData[]> {
    try {
      const { data, error } = await supabase
        .from('crops')
        .select(`
          *,
          crop_county_data(*),
          market_trends(*),
          growing_conditions(*),
          best_practices(*)
        `);

      if (error) {
        console.error('Error fetching crops from database:', error);
        return this.getMockCrops();
      }

      return data?.map(this.transformCropData) || [];
    } catch (error) {
      console.error('Database connection error:', error);
      return this.getMockCrops();
    }
  }

  // Product/Marketplace methods
  static async getProducts(filters?: {
    category?: string;
    searchTerm?: string;
    minPrice?: number;
    maxPrice?: number;
    location?: string;
    shopId?: string;
  }) {
    try {
      // 1. Fetch products with shop details
      let query = supabase
        .from('products')
        .select(`
          *,
          shop:shops(name, slug)
        `);

      if (filters?.shopId) {
        query = query.eq('shop_id', filters.shopId);
      }

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }

      if (filters?.searchTerm) {
        query = query.or(`name.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
      }

      if (filters?.minPrice) {
        query = query.gte('price', filters.minPrice);
      }

      if (filters?.maxPrice) {
        query = query.lte('price', filters.maxPrice);
      }

      const { data: products, error: productError } = await query;

      if (productError) throw productError;

      // 2. Fetch active campaigns
      const now = new Date().toISOString();
      const { data: campaigns, error: campaignError } = await supabase
        .from('shop_promotions')
        .select('*')
        .eq('is_active', true)
        .lte('start_date', now)
        .gte('end_date', now);

      if (campaignError) console.error('Error fetching campaigns:', campaignError);

      // 3. Apply campaign discounts to products
      return products?.map(p => {
        let finalPrice = Number(p.price);
        let originalPrice = null;
        let appliedCampaign = null;

        if (campaigns) {
          // Find campaigns for this shop
          const shopCampaigns = campaigns.filter(c => c.shop_id === p.shop_id);
          
          for (const campaign of shopCampaigns) {
            const isTargeted = 
              campaign.target_type === 'all_products' || 
              (campaign.target_type === 'specific_products' && campaign.target_ids.includes(p.id));

            if (isTargeted) {
              originalPrice = finalPrice;
              finalPrice = Math.round(finalPrice * (1 - campaign.discount_percent / 100));
              appliedCampaign = campaign.holiday_name;
              break; // Apply first matching campaign
            }
          }
        }

        return {
          ...p,
          price: finalPrice,
          originalPrice: originalPrice,
          campaign_name: appliedCampaign,
          shop_slug: (p as any).shop?.slug,
          shop_name: (p as any).shop?.name
        };
      }) || [];
    } catch (error) {
      console.error('Database connection error:', error);
      return [];
    }
  }

  static async addProduct(productData: any) {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert([productData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  }

  static async updateProduct(id: string | number, updates: any) {
    try {
      const { data, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  static async deleteProduct(id: string | number) {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  static async getMarketplaceProducts(filters?: {
    category?: string;
    searchTerm?: string;
    minPrice?: number;
    maxPrice?: number;
    location?: string;
  }) {
    try {
      // Simplified query to use the new 'products' table
      let query = supabase
        .from('products')
        .select('*');

      if (filters?.category) {
        query = query.eq('category', filters.category);
      }

      if (filters?.searchTerm) {
        query = query.or(`name.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
      }

      if (filters?.minPrice) {
        query = query.gte('price', filters.minPrice);
      }

      if (filters?.maxPrice) {
        query = query.lte('price', filters.maxPrice);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching marketplace products:', error);
        return this.getMockProducts();
      }

      // Map database fields to frontend model
      return data?.map(item => ({
        ...item,
        seller: { name: 'Verified Shop' } // Simplified for now
      })) || [];
    } catch (error) {
      console.error('Database connection error:', error);
      return this.getMockProducts();
    }
  }

  static async deleteMarketplaceProduct(id: string | number) {
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('marketplace')
        .select('*')
        .eq('id', id)
        .single();
      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('marketplace')
        .delete()
        .eq('id', id);


      const { data: authData, error: authError } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!authError && user && existing) {
        const { error: _auditError } = await supabase.from('audit_logs').insert([
          {
            table_name: 'marketplace',
            record_id: existing.id,
            action: 'delete',
            old_data: JSON.stringify(existing),
            new_data: null,
            edited_by: user.id,
          },
        ]);
      }
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting marketplace product:', error);
      throw error;
    }
  }

  // Order management methods
  static async createOrder(orderData: {
    userId: string;
    items: Array<{
      productId: string;
      quantity: number;
      price: number;
      name: string;
    }>;
    totalAmount: number;
    shippingAddress: any;
    paymentMethod: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .insert({
          user_id: orderData.userId,
          total_amount: orderData.totalAmount,
          status: 'pending',
          shipping_address: orderData.shippingAddress,
          payment_method: orderData.paymentMethod,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Insert order items
      const orderItems = orderData.items.map(item => ({
        order_id: data.id,
        product_id: item.productId,
        quantity: item.quantity,
        price: item.price,
        product_name: item.name,
        total_price: item.quantity * item.price
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      return data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  }

  static async getUserOrders(userId: string) {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user orders:', error);
      return [];
    }
  }

  static async getShopRevenue(shopId: string) {
    try {
      const { data, error } = await supabase
        .from('order_items')
        .select(`
          total_price,
          product:products!inner(shop_id)
        `)
        .eq('products.shop_id', shopId);

      if (error) throw error;
      
      const totalRevenue = data.reduce((sum, item) => sum + Number(item.total_price), 0);
      return totalRevenue;
    } catch (error) {
      console.error('Error calculating shop revenue:', error);
      return 0;
    }
  }

  static async getShopOrders(shopId: string) {
    try {
      const { data, error } = await supabase
        .from('order_items')
        .select(`
          *,
          order:orders(*)
        `)
        .eq('product:products.shop_id', shopId); // This might need a join or specific query

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching shop orders:', error);
      return [];
    }
  }

  static async updateOrderStatus(orderId: string, status: string) {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status })
        .eq('id', orderId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  }

  // Logistics methods
  static async getLogisticsProviders(filters?: {
    location?: string;
    vehicleType?: string;
    capacity?: number;
  }) {
    try {
      let query = supabase
        .from('logistics_companies')
        .select(`
          *,
          vehicles(*)
        `);

      if (filters?.location) {
        query = query.ilike('address', `%${filters.location}%`);
      }

      if (filters?.vehicleType) {
        // This filtering is complex on joined table, simplifying for now
        // Ideally use: !inner join or separate filter
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching logistics providers:', error);
        return this.getMockLogisticsProviders();
      }

      return data || [];
    } catch (error) {
      console.error('Database connection error:', error);
      return this.getMockLogisticsProviders();
    }
  }

  static async createLogisticsRequest(requestData: {
    userId: string;
    providerId: string;
    pickupLocation: any;
    deliveryLocation: any;
    cargoDetails: any;
    scheduledDate: string;
  }) {
    try {
      const { data, error } = await supabase
        .from('logistics_requests')
        .insert({
          user_id: requestData.userId,
          provider_id: requestData.providerId,
          pickup_location: requestData.pickupLocation,
          delivery_location: requestData.deliveryLocation,
          cargo_details: requestData.cargoDetails,
          scheduled_date: requestData.scheduledDate,
          status: 'pending',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating logistics request:', error);
      throw error;
    }
  }

  static async getLogisticsRequests(userId: string) {
    try {
      const { data, error } = await supabase
        .from('logistics_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching logistics requests:', error);
      return [];
    }
  }

  // Transform methods for database data
  private static transformCountyData(dbData: any): CountyData {
    return {
      name: dbData.name,
      region: dbData.region,
      climate: {
        temperature: dbData.temperature_range,
        rainfall: dbData.rainfall_range,
        altitude: dbData.altitude_range
      },
      mainCrops: dbData.main_crops || [],
      governmentRequirements: {
        licenses: dbData.required_licenses || [],
        permits: dbData.required_permits || [],
        regulations: dbData.applicable_regulations || []
      },
      marketPrices: dbData.market_prices?.map((price: any) => ({
        commodity: price.commodity,
        price: price.price,
        unit: price.unit,
        date: price.date
      })) || [],
      farmingTips: dbData.farming_tips?.map((tip: any) => ({
        category: tip.category,
        tips: tip.tips || []
      })) || [],
      resources: dbData.resources?.map((resource: any) => ({
        title: resource.title,
        url: resource.url,
        description: resource.description
      })) || [],
      lastUpdated: new Date(dbData.updated_at || dbData.created_at)
    };
  }

  private static transformCropData(crop: any): CropData {
    return {
      cropName: crop.name,
      countyData: crop.crop_county_data?.reduce((acc: Record<string, number>, county: { county_name: string; yield_score: number }) => {
        acc[county.county_name] = county.yield_score;
        return acc;
      }, {}) || {},
      nationalAverage: crop.national_average || 0,
      internationalComparison: crop.international_comparison || {},
      marketTrend: crop.market_trends?.map((trend: { month: string; price: number }) => ({
        month: trend.month,
        price: trend.price
      })) || [],
      growingConditions: {
        temperature: crop.growing_conditions?.temperature,
        rainfall: crop.growing_conditions?.rainfall,
        soil: crop.growing_conditions?.soil,
        altitude: crop.growing_conditions?.altitude
      },
      bestPractices: crop.best_practices?.practices || [],
      dataSource: crop.data_source || 'Database'
    };
  }

  // Fallback mock data methods
  private static getMockCounties(): CountyData[] {
    // Return existing mock data as fallback
    return [
      {
        name: 'Nairobi',
        region: 'Central',
        climate: {
          temperature: '18-26°C',
          rainfall: '800-1200mm annually',
          altitude: '1600-1850m above sea level'
        },
        mainCrops: ['Vegetables', 'Fruits', 'Flowers'],
        governmentRequirements: {
          licenses: ['County Business Permit', 'Health Certificate'],
          permits: ['Land Use Permit', 'Water Usage Permit'],
          regulations: ['Environmental Protection Act', 'Public Health Act']
        },
        marketPrices: [
          { commodity: 'Tomatoes', price: 120, unit: 'Kg', date: '2023-06-01' },
          { commodity: 'Potatoes', price: 45, unit: 'Kg', date: '2023-06-01' }
        ],
        farmingTips: [],
        resources: [],
        lastUpdated: new Date()
      }
    ];
  }

  private static getMockCountyByName(name: string): CountyData | null {
    const counties = this.getMockCounties();
    return counties.find(county => county.name.toLowerCase() === name.toLowerCase()) || null;
  }

  private static getMockCropData(name: string): CropData | null {
    // Return mock crop data as fallback
    return {
      cropName: name,
      countyData: {},
      nationalAverage: 70,
      internationalComparison: {},
      marketTrend: [],
      growingConditions: {
        temperature: 'Variable',
        rainfall: 'Variable',
        soil: 'Variable',
        altitude: 'Variable'
      },
      bestPractices: [],
      dataSource: 'Mock Data'
    };
  }

  private static getMockCrops(): CropData[] {
    return ['Maize', 'Coffee', 'Tea', 'Rice'].map(name => this.getMockCropData(name)!);
  }

  private static getMockProducts() {
    return [
      {
        id: '123e4567-e89b-12d3-a456-426614174001',
        name: 'Premium Maize Seeds',
        category: 'Seeds',
        price: 1200,
        unit: 'kg',
        rating: 4.5,
        stock: 500,
        description: 'High-yield maize seeds suitable for various climates.',
        seller: { name: 'Kenya Seed Company', location: 'Nairobi' }
      }
    ];
  }

  private static getMockLogisticsProviders() {
    return [
      {
        id: 1,
        name: 'Fast Transport Ltd',
        vehicle_type: 'Truck',
        capacity: 5000,
        rating: 4.5,
        price_per_km: 50,
        service_areas: ['Nairobi', 'Kiambu', 'Nakuru']
      }
    ];
  }
  static async createMarketplaceProduct(productData: {
    name: string;
    category: string;
    price: number;
    unit: string;
    description?: string;
    stock: number;
    farm_id: string;
    seller_id: string;
    images?: string[];
  }): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('marketplace')
        .insert({
          title: productData.name,
          category: productData.category,
          price: productData.price,
          unit: productData.unit,
          description: productData.description || '',
          stock: productData.stock,
          farm_id: productData.farm_id,
          user_id: productData.seller_id,
          images: productData.images && productData.images.length > 0 ? productData.images : null,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating marketplace product:', error);
      throw error;
    }
  }
}

export default DataService;
